import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "../lib/api";
import { Card, PageHeader, Btn, Pill, Input, Select, Textarea } from "../components/UI";
import { Tv, ChevronLeft, ChevronRight, Sparkles, Check, ShieldCheck } from "lucide-react";

const CTV_OBJECTIVES = [
  "Candidate awareness", "Name recognition", "Issue education", "Ballot awareness",
  "District reach", "Message reinforcement", "Traditional-TV extension",
  "Reach expansion", "Frequency", "Fundraising promotion", "Rapid response", "Final campaign reach"
];

const STEPS = ["Entity", "Objective", "Geography", "Audience", "Budget", "Creative", "Forecast", "Compliance & Launch"];

export default function CTVCampaignBuilder() {
  const navigate = useNavigate();
  const [step, setStep] = useState(0);
  const [entities, setEntities] = useState([]);
  const [audiences, setAudiences] = useState([]);
  const [providers, setProviders] = useState([]);
  const [pricing, setPricing] = useState(null);
  const [form, setForm] = useState({
    political_entity_id: "",
    objective: "Candidate awareness",
    dma: "", state: "TX", district: "",
    total_budget: 50000, daily_budget: 1500,
    audience_ids: [], provider_segment_ids: [],
    creative_notes: "",
    frequency_cap_daily: 3, frequency_cap_weekly: 8,
    est_impressions: 0, est_reach: 0, est_completions: 0, est_cpm: 32, est_cpcv: 0.036,
    disclaimer_confirmed: false, human_approval_confirmed: false,
  });

  useEffect(() => {
    (async () => {
      const [e, a, p] = await Promise.all([
        api.get("/political-entities"),
        api.get("/audiences"),
        api.get("/political-data/providers?status=available"),
      ]);
      setEntities(e.data); setAudiences(a.data); setProviders(p.data.filter(x => x.ctv_eligible));
      if (e.data[0]) setForm(f => ({ ...f, political_entity_id: e.data[0].id }));
      // fetch effective CTV pricing
      try {
        const pr = await api.get("/pricing/effective?channel=ctv");
        setPricing(pr.data);
      } catch { /* not admin */ }
    })();
  }, []);

  const activeEntity = entities.find(e => e.id === form.political_entity_id);

  // Recompute forecast on budget change
  useEffect(() => {
    const impressions = Math.round(form.total_budget / form.est_cpm * 1000);
    const completions = Math.round(impressions * 0.94);
    const reach = Math.round(impressions / form.frequency_cap_daily * 0.8);
    setForm(f => ({ ...f, est_impressions: impressions, est_completions: completions, est_reach: reach, est_cpcv: (form.total_budget / (completions || 1)).toFixed(3) }));
    // eslint-disable-next-line
  }, [form.total_budget, form.est_cpm, form.frequency_cap_daily]);

  const toggle = (key, val) => setForm(f => ({ ...f, [key]: f[key].includes(val) ? f[key].filter(v => v !== val) : [...f[key], val] }));

  const launch = async (asDraft = false) => {
    if (!activeEntity) { alert("Select a political entity"); return; }
    if (!asDraft && (!form.disclaimer_confirmed || !form.human_approval_confirmed)) {
      alert("Compliance checkboxes required"); return;
    }
    // Create as a Campaign under the political entity (via existing campaigns API for now)
    // Ensure a client_id exists for the entity — check if a mirror client exists.
    const clients = (await api.get("/clients")).data;
    let mirror = clients.find(c => c.business_name === (activeEntity.public_campaign_name || activeEntity.legal_org_name));
    if (!mirror) {
      const created = await api.post("/clients", {
        business_name: activeEntity.public_campaign_name || activeEntity.legal_org_name,
        industry: "Political",
        category: activeEntity.entity_type,
        monthly_budget: activeEntity.monthly_budget || 0,
        status: "Active",
        is_political: true,
      });
      mirror = created.data;
    }
    const payload = {
      name: `CTV · ${form.objective} · ${activeEntity.public_campaign_name || activeEntity.legal_org_name}`,
      client_id: mirror.id,
      objective: "Political / public affairs",
      total_budget: parseFloat(form.total_budget),
      daily_budget: parseFloat(form.daily_budget),
      timezone: "America/Chicago",
      category: "Political",
      billing_model: "CPM",
      margin: (pricing?.markup_percent || 55) / 100,
      status: asDraft ? "Draft" : "Pending approval",
      channels: ["Connected TV"],
      inventory_sources: ["Programmatic"],
      targeting: { state: form.state, dma: form.dma, district: form.district },
      creatives: [],
      audiences: form.audience_ids,
      polygons: [],
      cpm_bid: form.est_cpm,
      max_bid: form.est_cpm * 1.5,
      bid_strategy: "Maximize household reach",
      frequency_cap: form.frequency_cap_daily,
      political_disclaimer: activeEntity.approved_disclaimers?.[0] || "",
    };
    await api.post("/campaigns", payload);
    navigate("/campaigns");
  };

  return (
    <div className="p-6 lg:p-8 max-w-6xl mx-auto">
      <div className="flex items-center gap-3 mb-6">
        <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-cyan-400 to-blue-500 flex items-center justify-center">
          <Tv className="w-5 h-5 text-slate-900" />
        </div>
        <div className="flex-1">
          <div className="text-xs uppercase tracking-widest text-cyan-300 font-medium">Political CTV Campaign Builder</div>
          <div className="font-display text-2xl lg:text-3xl font-bold tracking-tight">Reach voters on every screen.</div>
        </div>
        <Btn variant="secondary" onClick={() => launch(true)}>Save draft</Btn>
      </div>

      <Card className="mb-6" padded={false}>
        <div className="flex overflow-x-auto">
          {STEPS.map((s, i) => (
            <button
              key={s}
              onClick={() => setStep(i)}
              data-testid={`ctv-step-${i}`}
              className={`flex-1 min-w-[110px] px-4 py-3 text-xs uppercase tracking-wider font-medium border-b-2 transition-colors ${
                i === step ? "border-cyan-400 text-cyan-300 bg-cyan-400/5" : i < step ? "border-cyan-400/30 text-slate-300" : "border-transparent text-slate-500 hover:text-slate-300"
              }`}
            >
              <span className="mr-1.5">{i + 1}.</span> {s}
            </button>
          ))}
        </div>
      </Card>

      <Card>
        {step === 0 && (
          <div className="space-y-4" data-testid="ctv-step-entity">
            <Select label="Political entity" value={form.political_entity_id} onChange={(e) => setForm({ ...form, political_entity_id: e.target.value })}>
              <option value="">Select…</option>
              {entities.map(e => <option key={e.id} value={e.id}>{e.public_campaign_name || e.legal_org_name}</option>)}
            </Select>
            {activeEntity && (
              <div className="rounded-md border border-cyan-400/30 bg-cyan-400/5 p-3 text-sm">
                <div className="text-cyan-300 font-semibold">{activeEntity.public_campaign_name || activeEntity.legal_org_name}</div>
                <div className="text-slate-300 text-xs mt-1">{activeEntity.entity_type?.replace(/_/g, " ")} · {activeEntity.authorization_status?.replace(/_/g, " ")} · Verification: {activeEntity.verification_status}</div>
                {activeEntity.approved_disclaimers?.[0] && <div className="text-xs italic text-slate-400 mt-2">{activeEntity.approved_disclaimers[0]}</div>}
              </div>
            )}
          </div>
        )}

        {step === 1 && (
          <div className="grid grid-cols-2 md:grid-cols-3 gap-3" data-testid="ctv-step-objective">
            {CTV_OBJECTIVES.map(o => {
              const active = form.objective === o;
              return (
                <button
                  key={o}
                  onClick={() => setForm({ ...form, objective: o })}
                  className={`text-left px-3 py-2.5 rounded-md border text-sm transition-all ${
                    active ? "bg-gradient-to-r from-cyan-400/20 to-blue-500/10 border-cyan-400/50 text-white" : "bg-white/[0.02] border-white/10 text-slate-300 hover:border-white/20"
                  }`}
                >
                  <div className="flex items-center justify-between">
                    <span>{o}</span>{active && <Check className="w-4 h-4 text-cyan-300" />}
                  </div>
                </button>
              );
            })}
          </div>
        )}

        {step === 2 && (
          <div className="grid grid-cols-2 gap-4" data-testid="ctv-step-geo">
            <Input label="State" value={form.state} onChange={(e) => setForm({ ...form, state: e.target.value })} />
            <Input label="DMA" value={form.dma} onChange={(e) => setForm({ ...form, dma: e.target.value })} placeholder="Dallas-Ft. Worth" />
            <Input label="District" value={form.district} onChange={(e) => setForm({ ...form, district: e.target.value })} placeholder="TX-32 / SD-12" />
            <div className="col-span-2 rounded-md border border-white/10 p-3 text-xs text-slate-400">
              <ShieldCheck className="w-4 h-4 text-cyan-300 inline mr-1" />
              District & precinct polygons can be drawn in the <button onClick={() => navigate("/geo")} className="text-cyan-300 underline">Geo module</button>.
            </div>
          </div>
        )}

        {step === 3 && (
          <div className="space-y-4" data-testid="ctv-step-audience">
            <div>
              <div className="text-xs uppercase tracking-wider text-slate-400 font-medium mb-2">Available political data (CTV-eligible)</div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-2 max-h-72 overflow-y-auto">
                {providers.map(p => {
                  const active = form.provider_segment_ids.includes(p.id);
                  return (
                    <button
                      key={p.id}
                      onClick={() => toggle("provider_segment_ids", p.id)}
                      className={`text-left rounded-md border px-3 py-2 text-xs transition-colors ${active ? "bg-cyan-400/10 border-cyan-400/40" : "bg-white/[0.02] border-white/10 hover:border-white/20"}`}
                    >
                      <div className="font-semibold text-white">{p.segment_name}</div>
                      <div className="text-[10px] text-slate-400 mt-0.5">{p.provider} · {p.category} · Reach {p.estimated_reach.toLocaleString()}</div>
                    </button>
                  );
                })}
              </div>
            </div>
            {audiences.length > 0 && (
              <div>
                <div className="text-xs uppercase tracking-wider text-slate-400 font-medium mb-2">Your saved audiences</div>
                <div className="flex flex-wrap gap-2">
                  {audiences.map(a => (
                    <button
                      key={a.id}
                      onClick={() => toggle("audience_ids", a.id)}
                      className={`text-xs px-3 py-1.5 rounded-full border ${form.audience_ids.includes(a.id) ? "bg-cyan-400/20 border-cyan-400/40 text-cyan-300" : "bg-white/5 border-white/10 text-slate-300"}`}
                    >
                      {a.name}
                    </button>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}

        {step === 4 && (
          <div className="grid grid-cols-2 gap-4" data-testid="ctv-step-budget">
            <Input label="Total budget (USD)" type="number" value={form.total_budget} onChange={(e) => setForm({ ...form, total_budget: parseFloat(e.target.value) })} />
            <Input label="Daily budget (USD)" type="number" value={form.daily_budget} onChange={(e) => setForm({ ...form, daily_budget: parseFloat(e.target.value) })} />
            <Input label="Estimated CPM (USD)" type="number" step="0.5" value={form.est_cpm} onChange={(e) => setForm({ ...form, est_cpm: parseFloat(e.target.value) })} />
            <Input label="Daily frequency cap" type="number" value={form.frequency_cap_daily} onChange={(e) => setForm({ ...form, frequency_cap_daily: parseInt(e.target.value) || 1 })} />
            {pricing && (
              <div className="col-span-2 rounded-md border border-cyan-400/30 bg-cyan-400/5 p-3 text-xs">
                <div className="text-cyan-300 font-semibold">Pricing: {pricing.markup_percent}% markup · source: {pricing.source}</div>
                <div className="text-slate-400 mt-1">Client sees the campaign budget; media cost and markup are internal.</div>
              </div>
            )}
          </div>
        )}

        {step === 5 && (
          <div className="space-y-4" data-testid="ctv-step-creative">
            <Textarea label="CTV creative notes / brief" value={form.creative_notes} onChange={(e) => setForm({ ...form, creative_notes: e.target.value })} placeholder="30-second spot explaining the candidate's economic message. Upload finished video via the Creatives library." />
            <div className="flex gap-2">
              <Btn variant="secondary" onClick={() => navigate("/creative-studio")}><Sparkles className="w-4 h-4" /> Open CTV Creative Studio</Btn>
              <Btn variant="secondary" onClick={() => navigate("/creatives")}>Upload finished video</Btn>
            </div>
          </div>
        )}

        {step === 6 && (
          <div data-testid="ctv-step-forecast">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              <Forecast label="Est. impressions" v={form.est_impressions.toLocaleString()} />
              <Forecast label="Est. household reach" v={form.est_reach.toLocaleString()} />
              <Forecast label="Est. completed views" v={form.est_completions.toLocaleString()} />
              <Forecast label="Est. cost / completed view" v={`$${form.est_cpcv}`} accent />
            </div>
            <div className="mt-4 text-xs text-slate-400">Forecast is an estimate based on the CPM you set and completion benchmarks. Never guarantees election outcomes.</div>
          </div>
        )}

        {step === 7 && (
          <div className="space-y-3" data-testid="ctv-step-compliance">
            <div className="rounded-md border border-amber-400/30 bg-amber-400/5 p-4 flex gap-3">
              <ShieldCheck className="w-5 h-5 text-amber-300 shrink-0" />
              <div>
                <div className="text-amber-300 font-semibold text-sm">Political compliance gate</div>
                <div className="text-xs text-slate-300 mt-1">This campaign will go through disclaimer, entity, audience, creative, and human approval reviews before ad platform activation.</div>
              </div>
            </div>
            <label className="flex items-start gap-2 text-sm text-slate-200">
              <input type="checkbox" checked={form.disclaimer_confirmed} onChange={(e) => setForm({ ...form, disclaimer_confirmed: e.target.checked })} className="mt-1" data-testid="ctv-check-disclaimer" />
              I confirm the "Paid for by" disclaimer will be present in every deliverable.
            </label>
            <label className="flex items-start gap-2 text-sm text-slate-200">
              <input type="checkbox" checked={form.human_approval_confirmed} onChange={(e) => setForm({ ...form, human_approval_confirmed: e.target.checked })} className="mt-1" data-testid="ctv-check-human" />
              I confirm a human compliance reviewer will approve before launch.
            </label>
            <Btn onClick={() => launch(false)} data-testid="ctv-submit" className="mt-4">Submit for compliance review</Btn>
          </div>
        )}
      </Card>

      <div className="mt-6 flex items-center justify-between">
        <Btn variant="secondary" onClick={() => setStep(Math.max(0, step - 1))} disabled={step === 0}><ChevronLeft className="w-4 h-4" /> Back</Btn>
        <div className="text-xs text-slate-500">Step {step + 1} of {STEPS.length}</div>
        <Btn onClick={() => setStep(Math.min(STEPS.length - 1, step + 1))} disabled={step === STEPS.length - 1}>Next <ChevronRight className="w-4 h-4" /></Btn>
      </div>
    </div>
  );
}

const Forecast = ({ label, v, accent }) => (
  <div className="rounded-md border border-white/10 p-3 bg-white/[0.02]">
    <div className="text-[10px] uppercase tracking-wider text-slate-500">{label}</div>
    <div className={`font-display text-2xl font-bold mt-1 ${accent ? "brand-gradient" : ""}`}>{v}</div>
  </div>
);
